// PdfViewer.jsx

import React, { useCallback, useRef, useMemo } from 'react';
import PropTypes from 'prop-types';

const fileSrc = (id) => `/drive.php?action=img&id=${encodeURIComponent(id)}`;

export default function PdfViewer({ item, lb }) {
    const {
        onDoubleClick, onTouchStart, onTouchEnd,
        close,
        prev,
        next,
    } = lb;

    const src = fileSrc(item.id);

    // refs для double-tap + swipe
    const lastTapRef = useRef(0);
    const touchStartXRef = useRef(0);
    const touchStartYRef = useRef(0);

    const SWIPE_THRESHOLD = 50; // px

    // вважаємо, що мобільний, якщо pointer:coarse (тач)
    const isMobile = useMemo(() => {
        if (typeof window === 'undefined') return false;
        return window.matchMedia && window.matchMedia('(pointer: coarse)').matches;
    }, []);

    const handleClick = (e) => {
        // не даємо події піти нагору
        e.stopPropagation();
    };

    // 👇 Обгортки для touch-ів: double tap (ESC) + swipe
    const handleTouchStart = useCallback(
        (e) => {
            const now = Date.now();
            const delta = now - lastTapRef.current;
            lastTapRef.current = now;

            // якщо два тапи за короткий час — діємо як ESC → закриваємо лайтбокс
            if (delta > 0 && delta < 300) {
                e.stopPropagation();
                if (typeof close === 'function') {
                    close();
                }
                // не викликаємо onDoubleClick, не рухаємо swipe
                return;
            }

            // зберігаємо стартову позицію для swipe
            if (e.touches && e.touches.length === 1) {
                const t = e.touches[0];
                touchStartXRef.current = t.clientX;
                touchStartYRef.current = t.clientY;
            }

            if (typeof onTouchStart === 'function') {
                onTouchStart(e);
            }
        },
        [close, onTouchStart]
    );

    const handleTouchEnd = useCallback(
        (e) => {
            // swipe детекція
            const touch = e.changedTouches && e.changedTouches[0];
            if (touch) {
                const dx = touch.clientX - touchStartXRef.current;
                const dy = touch.clientY - touchStartYRef.current;

                if (Math.abs(dx) > SWIPE_THRESHOLD && Math.abs(dx) > Math.abs(dy)) {
                    // swipe горизонтальний
                    if (dx > 0) {
                        // свайп вправо → попереднє зображення
                        if (typeof prev === 'function') prev();
                    } else {
                        // свайп вліво → наступне зображення
                        if (typeof next === 'function') next();
                    }
                }
            }

            if (typeof onTouchEnd === 'function') {
                onTouchEnd(e);
            }
        },
        [onTouchEnd, prev, next]
    );

    return (
        <div
            className="relative overflow-hidden rounded-xl shadow-2xl bg-white"
            style={{ height: '78vh', width: '92vw' }}
            onClick={handleClick}
            onTouchStart={handleTouchStart}
            onTouchEnd={handleTouchEnd}
            onDoubleClick={onDoubleClick}   // ⬅️ desktop double-click як раніше
        >
            {/* Кнопки навігації тільки на десктопі */}
            {!isMobile && prev && (
                <button
                    type="button"
                    className="absolute left-3 top-1/2 -translate-y-1/2 z-10 bg-white/70 rounded-full px-3 py-1 shadow"
                    onClick={(e) => {
                        e.stopPropagation();
                        prev();
                    }}
                >
                    ←
                </button>
            )}

            {!isMobile && next && (
                <button
                    type="button"
                    className="absolute right-3 top-1/2 -translate-y-1/2 z-10 bg-white/70 rounded-full px-3 py-1 shadow"
                    onClick={(e) => {
                        e.stopPropagation();
                        next();
                    }}
                >
                    →
                </button>
            )}

            <iframe
                key={item.id}
                title={item.name || 'PDF'}
                src={src}
                className="w-full h-full"
            />
        </div>
    );
}

PdfViewer.propTypes = {
    item: PropTypes.shape({
        id: PropTypes.string.isRequired,
        name: PropTypes.string,
    }).isRequired,
    lb: PropTypes.object.isRequired,
};

